import AppStoreBadge from '@/components/AppStoreBadge';
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calculator, TrendingUp, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { images } from '@/assets/images';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { PageTransition } from '@/components/layout/PageTransition';
import RecentCalculationsCard from '@/components/calculator/RecentCalculationsCard';
import SEOHead from '@/components/seo/SEOHead';
import SEOFooter from '@/components/layout/SEOFooter';
import { FactorPaymentsChart } from '@/components/calculator/FactorPaymentsChart';

const TryCalculatorLanding: React.FC = () => {
  const { isAuthenticated } = useAuth();

  const calculators = [
    {
      title: 'Mortgage Payment Calculator',
      description: 'Enter your home price and loan details to calculate your monthly mortgage payment breakdown including principal, interest, taxes, and insurance (PITI).',
      icon: Calculator,
      path: '/try/payment',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      iconColor: 'text-purple-400',
    },
    {
      title: 'Reverse PITI Calculator',
      description: 'Enter your desired monthly payment to find the maximum home price you can afford. Perfect for budget-first planning and home affordability analysis.',
      icon: TrendingUp,
      path: '/try/reverse-piti',
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/30',
      iconColor: 'text-cyan-400',
    },
  ];

  return (
    <div className="duo-page min-h-dvh bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950 flex flex-col">
      <SEOHead
        title="Free Mortgage Calculator — Calculate Monthly Payments | EquiForge"
        description="Use our free mortgage calculator to estimate monthly payments including principal, interest, taxes, and insurance (PITI). Try the reverse PITI calculator to find how much house you can afford."
        keywords="free mortgage calculator, monthly mortgage payment calculator, PITI calculator, reverse PITI, home loan calculator, how much house can I afford, mortgage payment estimator, home affordability calculator, closing cost calculator, first time homebuyer calculator"
        canonicalPath="/try"
      />

      {/* Header */}
      <header className="calculator-header sticky top-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-border/40">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 max-w-7xl mx-auto">
          <Link to="/home" className="flex items-center gap-2">
            <img
              src={images.equiforgeLogoFlat}
              alt="EquiForge — Free Mortgage Calculator"
              className="h-8 md:h-10 w-auto object-contain"
            />
          </Link>

          <div className="flex items-center gap-2">
            {!isAuthenticated && (
              <>
                <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                  <Link to="/login">Sign In</Link>
                </Button>
                <Button asChild size="sm" className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700">
                  <Link to="/signup">Sign Up Free</Link>
                </Button>
              </>
            )}
            {isAuthenticated && (
              <Button asChild variant="outline" size="sm" className="border-border/60">
                <Link to="/calculator">Go to Dashboard</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center px-4 py-12">
        <PageTransition>
          <div className="flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-8"
            >
              <h1 className="text-3xl md:text-4xl font-bold mb-3">
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                  Free Mortgage Calculators
                </span>
              </h1>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto">
                Calculate your monthly mortgage payment or find out how much house you can afford — no signup required
              </p>
              <div className="mt-6 flex flex-col items-center gap-2">
                <p className="text-sm text-slate-300">Prefer the iOS app?</p>
                <AppStoreBadge />
              </div>
            </motion.div>

            {/* Recent Calculations for Authenticated Users */}
            {isAuthenticated && (
              <div className="mb-8">
                <RecentCalculationsCard />
              </div>
            )}

            {/* Calculator Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl w-full">
              {calculators.map((calc, index) => (
                <motion.div
                  key={calc.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link to={calc.path} className="block h-full">
                    <div className={`group relative h-full p-6 rounded-xl border ${calc.borderColor} ${calc.bgColor} backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-purple-500/10`}>
                      <div className={`inline-flex p-3 rounded-lg ${calc.bgColor} border ${calc.borderColor} mb-4`}>
                        <calc.icon className={`w-6 h-6 ${calc.iconColor}`} />
                      </div>
                      <h2 className="text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                        {calc.title}
                      </h2>
                      <p className="text-muted-foreground text-sm mb-6">
                        {calc.description}
                      </p>
                      <div className={`inline-flex items-center gap-2 text-sm font-medium bg-gradient-to-r ${calc.color} bg-clip-text text-transparent`}>
                        Calculate Now
                        <ArrowRight className={`w-4 h-4 ${calc.iconColor} transition-transform group-hover:translate-x-1`} />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Factor Payments Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.5 }}
              className="max-w-4xl w-full mt-10"
            >
              <FactorPaymentsChart defaultExpanded={false} />
            </motion.div>

            {/* SEO Content Block */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="max-w-4xl w-full mt-12 space-y-6"
            >
              <div className="text-center space-y-3 px-4">
                <p className="text-sm text-slate-400 leading-relaxed">
                  Use our <strong className="text-slate-300">free mortgage calculator</strong> to estimate your monthly payments including principal, interest, taxes, and insurance (PITI). 
                  Our <Link to="/try/reverse-piti" className="text-cyan-400 hover:underline">reverse PITI calculator</Link> helps you determine <strong className="text-slate-300">how much house you can afford</strong> based on your desired monthly budget. 
                  Whether you're a <strong className="text-slate-300">first-time homebuyer</strong> or an experienced investor, EquiForge provides the most comprehensive <strong className="text-slate-300">home loan calculator</strong> and <strong className="text-slate-300">real estate analytics</strong> tools available.
                </p>
                <p className="text-xs text-slate-500">
                  Trusted by thousands of homebuyers and real estate professionals. <Link to="/about" className="text-primary hover:underline">Learn more about EquiForge</Link> · <Link to="/brochure" className="text-primary hover:underline">View our brochure</Link> · <Link to="/compliance" className="text-primary hover:underline">Security & compliance</Link>
                </p>
              </div>
            </motion.div>
          </div>
        </PageTransition>
      </main>

      {/* Floating CTA Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        className="calculator-promo relative z-40 bg-gradient-to-r from-purple-900/90 via-slate-900/95 to-cyan-900/90 backdrop-blur-xl border-t border-border/40"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-slate-300">
              <span className="hidden sm:inline">Want SmartBuy comparison, AI insights & more? </span>
              <span className="sm:hidden">Unlock all tools </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild size="sm" className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700">
              <Link to="/signup">
                Sign Up Free
                <ArrowRight className="ml-1.5 w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="ghost" size="sm" className="text-slate-400 hover:text-slate-200 sm:flex hidden">
              <Link to="/home">Learn More</Link>
            </Button>
          </div>
        </div>
      </motion.div>

      <SEOFooter />
    </div>
  );
};

export default TryCalculatorLanding;
